import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, AlertCircle, Gift, Copy, Check } from 'lucide-react';
import { validateLeadForm } from '../lib/validate.js';
import { fetchFormToken, submitLead, isFormTokenError, SubmitError } from '../lib/crmApi.js';
import { currentUtm } from '../lib/utm.js';
import { CAMPAIGN_CODE } from '../lib/runtimeEnv.js';

const SPECIALTIES = [
  'پزشک عمومی',
  'داخلی',
  'قلب و عروق',
  'اطفال',
  'زنان و زایمان',
  'پوست و مو',
  'ارتوپدی',
  'گوش و حلق و بینی',
  'چشم‌پزشکی',
  'اعصاب و روان',
  'دندان‌پزشکی',
  'سایر',
];

// 16px text on phones is not a style choice: below it, iOS Safari zooms the
// whole page on focus and the visitor has to pinch back out mid-form. At this
// size the controls also land on 48px, over the 44px touch minimum.
const inputClass = (hasError) =>
  `w-full rounded-lg border bg-white px-4 py-3 text-base text-ink outline-none transition sm:text-sm ` +
  (hasError
    ? 'border-red focus:border-red'
    : 'border-line focus:border-navy focus:ring-2 focus:ring-navy-50');

function Field({ label, error, required, children }) {
  return (
    <label className="block text-right">
      <span className="mb-1.5 block text-sm font-medium text-ink">
        {label}
        {required && <span className="text-red"> *</span>}
      </span>
      {children}
      {error && (
        <span className="mt-1 flex items-center gap-1 text-xs text-red">
          <AlertCircle size={13} aria-hidden="true" />
          {error}
        </span>
      )}
    </label>
  );
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function LeadForm({ onSubmitted }) {
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    specialty: '',
    city: '',
    message: '',
    consent: false,
    website: '', // honeypot — hidden from people, filled by naive bots
  });
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState('idle'); // idle | sending | done
  const [reference, setReference] = useState(null);
  const [gift, setGift] = useState(null);
  const [copied, setCopied] = useState(false);
  const [serverError, setServerError] = useState(null);

  // The edge's anti-automation token: fetched on render so it is already
  // >= minAgeMs old by the time a human finishes typing.
  const tokenRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    fetchFormToken()
      .then((t) => {
        if (!cancelled) tokenRef.current = { ...t, fetchedAt: Date.now() };
      })
      .catch(() => {
        /* fetched again on submit */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (name) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  async function freshToken() {
    let entry = tokenRef.current;
    if (!entry) {
      entry = { ...(await fetchFormToken()), fetchedAt: Date.now() };
      tokenRef.current = entry;
    }
    // Too young (visitor submitted in under minAgeMs): wait out the remainder
    // rather than burning the token on a FORM_TOKEN_TOO_FAST round trip.
    const age = Date.now() - entry.fetchedAt;
    if (age < entry.minAgeMs) await sleep(entry.minAgeMs - age);
    return entry.token;
  }

  function buildPayload(formToken) {
    return {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      mobile: values.mobile.trim(),
      ...(values.specialty ? { specialty: values.specialty } : {}),
      ...(values.city.trim() ? { city: values.city.trim() } : {}),
      ...(values.message.trim() ? { message: values.message.trim() } : {}),
      consent: values.consent,
      source: 'campaign',
      product: 'doctor_panel',
      campaignCode: CAMPAIGN_CODE,
      utm: currentUtm(),
      landingUrl:
        typeof window !== 'undefined' ? window.location.href.slice(0, 300) : undefined,
      website: values.website,
      formToken,
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setServerError(null);

    const { errors: fieldErrors, ok } = validateLeadForm(values);
    setErrors(fieldErrors);
    if (!ok) return;

    setPhase('sending');
    try {
      let result;
      try {
        result = await submitLead(buildPayload(await freshToken()));
      } catch (err) {
        // A stale/used token is recoverable without bothering the visitor:
        // fetch a fresh one, wait out its minimum age, retry once.
        if (!isFormTokenError(err)) throw err;
        tokenRef.current = null;
        result = await submitLead(buildPayload(await freshToken()));
      }
      setReference(result.reference || null);
      setGift(result.gift || null);
      setPhase('done');
      onSubmitted?.();
    } catch (err) {
      setPhase('idle');
      if (err instanceof SubmitError && err.fields) {
        setErrors((prev) => ({ ...prev, ...err.fields }));
        setServerError(err.message);
      } else {
        setServerError(err.message || 'ثبت درخواست ممکن نشد. لطفاً دوباره تلاش کنید.');
      }
      // Whatever happened, the old token is spent.
      tokenRef.current = null;
      fetchFormToken()
        .then((t) => {
          tokenRef.current = { ...t, fetchedAt: Date.now() };
        })
        .catch(() => {});
    }
  }

  async function copyCode() {
    if (!gift?.code) return;
    try {
      await navigator.clipboard.writeText(gift.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked (insecure context, or the visitor denied it).
      // The code is on screen and selectable, so nothing is lost — but the
      // button must not claim it copied when it did not.
    }
  }

  if (phase === 'done') {
    return (
      <div className="rounded-lg bg-white p-5 text-center shadow-card sm:p-8" role="status">
        <CheckCircle2 className="mx-auto mb-4 text-green" size={56} aria-hidden="true" />
        <h3 className="mb-2 text-xl font-bold text-ink">درخواست شما ثبت شد</h3>
        <p className="text-sm leading-7 text-ink-soft">
          ممنون از اعتماد شما. کارشناسان پارسینا در اولین فرصت برای معرفی پنل پزشک و پاسخ به
          پرسش‌هایتان با شما تماس می‌گیرند.
        </p>

        {gift?.code && (
          <div className="mt-6 rounded-lg bg-gradient-brand p-[2px] shadow-lift">
            <div className="flex flex-col gap-3 rounded-[calc(0.625rem-2px)] bg-white p-5">
              <div className="flex items-center justify-center gap-2 text-navy">
                <Gift size={18} aria-hidden="true" />
                <span className="text-sm font-black text-ink">هدیهٔ روز پزشک شما</span>
              </div>

              {/* Codes run to DRDAY-7K4F-2M9A — 15 characters that must not
                  wrap. At text-xl/0.15em that overflows a 360px screen, so
                  phones get 18px and tighter tracking. */}
              <div
                dir="ltr"
                className="rounded-lg border-[1.5px] border-dashed border-navy-light bg-page px-3 py-3.5 text-lg font-black tracking-[0.08em] text-navy-dark sm:text-xl sm:tracking-[0.15em]"
              >
                {gift.code}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 text-[13px] sm:text-sm">
                {gift.smartVisits > 0 && (
                  <>
                    <span className="font-bold text-ink">
                      {gift.smartVisits.toLocaleString('fa-IR')} ویزیت هوشمند رایگان
                    </span>
                    <span className="h-1 w-1 shrink-0 rounded-full bg-ink-faint" aria-hidden="true" />
                  </>
                )}
                <span className="font-black text-teal">
                  {gift.parseh.toLocaleString('fa-IR')} پارسه
                </span>
              </div>

              <button
                type="button"
                onClick={copyCode}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-brand px-6 py-3 text-sm font-bold text-white transition hover:opacity-95"
              >
                {copied
                  ? (<><Check size={17} aria-hidden="true" />کپی شد</>)
                  : (<><Copy size={17} aria-hidden="true" />کپی کد</>)}
              </button>

              <p className="text-[11px] leading-6 text-ink-soft">
                این کد را نگه دارید. پس از فعال‌سازی حساب، آن را در بخش «کیف پارسه» پنل پزشک
                وارد کنید تا اعتبار به حساب شما اضافه شود.
              </p>
            </div>
          </div>
        )}

        {reference && (
          // The LABEL is Persian and the VALUE is a Latin uuid. Marking the
          // whole line dir="ltr" reorders the label around the value (the
          // trailing digit jumps ahead of the colon); only the uuid is LTR.
          <p className="mt-4 text-xs text-ink-faint">
            کد پیگیری: <span dir="ltr">{reference}</span>
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="relative rounded-lg bg-white p-4 shadow-card sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        {/* The two short name fields pair up even on a phone; `sm:contents`
            dissolves this wrapper so they rejoin the parent grid on desktop. */}
        <div className="grid grid-cols-2 gap-3 sm:contents">
          <Field label="نام" required error={errors.firstName}>
            <input
              className={inputClass(errors.firstName)}
              value={values.firstName}
              onChange={set('firstName')}
              autoComplete="given-name"
              name="firstName"
            />
          </Field>
          <Field label="نام خانوادگی" required error={errors.lastName}>
            <input
              className={inputClass(errors.lastName)}
              value={values.lastName}
              onChange={set('lastName')}
              autoComplete="family-name"
              name="lastName"
            />
          </Field>
        </div>
        <Field label="شماره موبایل" required error={errors.mobile}>
          <input
            className={inputClass(errors.mobile) + ' text-left'}
            value={values.mobile}
            onChange={set('mobile')}
            inputMode="tel"
            dir="ltr"
            placeholder="۰۹۱۲ ۳۴۵ ۶۷۸۹"
            autoComplete="tel"
            name="mobile"
          />
        </Field>
        <Field label="تخصص" error={errors.specialty}>
          <select
            className={inputClass(errors.specialty)}
            value={values.specialty}
            onChange={set('specialty')}
            name="specialty"
          >
            <option value="">انتخاب کنید…</option>
            {SPECIALTIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="شهر" error={errors.city}>
          <input
            className={inputClass(errors.city)}
            value={values.city}
            onChange={set('city')}
            autoComplete="address-level2"
            name="city"
          />
        </Field>
        <Field label="توضیح (اختیاری)" error={errors.message}>
          <input
            className={inputClass(errors.message)}
            value={values.message}
            onChange={set('message')}
            name="message"
            placeholder="مثلاً: ساعت مناسب تماس"
          />
        </Field>
      </div>

      {/* Honeypot: visually removed, still in the DOM for bots. No offscreen
          offset — in an RTL document a negative `left` extends the scrollable
          canvas; zero-size + opacity keeps layout untouched. */}
      <div
        className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        aria-hidden="true"
      >
        <label>
          وب‌سایت
          <input tabIndex={-1} autoComplete="off" value={values.website} onChange={set('website')} name="website" />
        </label>
      </div>

      <label className="mt-5 flex items-start gap-2.5 py-1 text-right">
        <input
          type="checkbox"
          checked={values.consent}
          onChange={set('consent')}
          className="mt-0.5 h-5 w-5 shrink-0 accent-[#0173BE] sm:mt-1 sm:h-4 sm:w-4"
          name="consent"
        />
        <span className="text-xs leading-6 text-ink-soft">
          موافقم کارشناسان پارسینا برای معرفی پنل پزشک از طریق تماس یا پیامک با من در ارتباط باشند.
        </span>
      </label>
      {errors.consent && (
        <p className="mt-1 flex items-center gap-1 text-xs text-red">
          <AlertCircle size={13} aria-hidden="true" />
          {errors.consent}
        </p>
      )}

      {serverError && (
        <div className="mt-4 rounded-lg border border-red/30 bg-red/5 p-3 text-sm text-red" role="alert">
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={phase === 'sending'}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-brand px-6 py-3.5 text-base font-bold text-white shadow-lift transition hover:opacity-95 disabled:opacity-60"
      >
        {phase === 'sending' ? (
          <>
            <Loader2 className="animate-spin" size={20} aria-hidden="true" />
            در حال ثبت…
          </>
        ) : (
          'ثبت درخواست مشاوره رایگان'
        )}
      </button>
      <p className="mt-3 text-center text-[11px] leading-5 text-ink-faint">
        اطلاعات شما فقط برای تماس تیم پارسینا استفاده می‌شود و در اختیار هیچ شخص ثالثی قرار نمی‌گیرد.
      </p>
    </form>
  );
}
